import React from "react";
import { useUser } from "src/shared/context/UserContext";

const permissionModules: Record<string, string[]> = {
  order: ["view", "create", "update", "delete", "cancel"],
  category: ["view", "create", "update", "delete"],
  product: ["view", "create", "update", "delete"],
  customer: ["view", "create", "update", "delete"],
  staff: ["view", "create", "update", "delete"],
  profile: ["view", "update"],
  user: ["view", "create", "update", "delete"],
  settings: ["view"],
  management: ["view"],
  dashboard: ["view"],
  catalog: ["view"],
  report: ["view", "delete"],
};

const allActions = ["view", "create", "update", "delete", "cancel"];

type RolePermissions = Record<string, string[]>;

type Props = {
  permissions: RolePermissions;
  setPermissions: React.Dispatch<React.SetStateAction<RolePermissions>>;
};

export default function PermissionTable({
  permissions,
  setPermissions,
}: Props) {
  const { user } = useUser();
  const businessType = user?.businessType;

  const getAllModules = () => {
  return Object.entries(permissionModules).filter(([module]) => {
    if (
      businessType?.toLowerCase() === "grocery" &&
      ["catalog", "report"].includes(module)
    ) {
      return false;
    }

    return true;
  });
};

const allModules = getAllModules();

  /** 🔒 Order dependency lock */
  const isDependencyLocked = (module: string) => {
    return (
      permissions["order"]?.includes("create") &&
      ["category", "product", "customer"].includes(module)
    );
  };

  /** 🔒 Management disable check */
  const isManagementDisabled = (perms: RolePermissions) => {
    const requiredModules = ["product", "category", "customer", "user"];

    return !requiredModules.some((module) =>
      (perms[module] || []).includes("view")
    );
  };

  /** 🔥 Global dependency enforcement */
  const enforceDependencies = (perms: RolePermissions): RolePermissions => {
    const updated = { ...perms };

    // ✅ Order Create dependency
    if (updated["order"]?.includes("create")) {
      ["category", "product", "customer"].forEach((module) => {
        const current = updated[module] || [];

        if (!current.includes("view")) {
          updated[module] = ["view", ...current];
        }
      });
    }

    // ✅ Management dependency
    if (isManagementDisabled(updated)) {
      updated["management"] = [];
    }

    return updated;
  };

  /** Toggle single permission */
  const togglePermission = (module: string, action: string) => {
    setPermissions((prev) => {
      const current = prev[module] || [];
      let updated: string[] = [];

      if (action === "view") {
        updated = current.includes("view") ? [] : ["view"];
      } else {
        if (!current.includes("view")) {
          updated = ["view", action];
        } else {
          updated = current.includes(action)
            ? current.filter((a) => a !== action)
            : [...current, action];
        }
      }

      return enforceDependencies({ ...prev, [module]: updated });
    });
  };

  /** Select / Deselect ALL */
  const toggleAll = () => {
    const allSelected = isAllSelected();

    if (allSelected) {
      setPermissions((prev) => enforceDependencies({}));
    } else {
      const newPermissions: RolePermissions = {};
      allModules.forEach(([module, actions]) => {
        newPermissions[module] = [...actions];
      });

      setPermissions(enforceDependencies(newPermissions));
    }
  };

  /** Select module row */
  const toggleModule = (module: string) => {
    const actions = permissionModules[module];
    const current = permissions[module] || [];

    const allSelected = actions.every((a) => current.includes(a));

    setPermissions((prev) =>
      enforceDependencies({
        ...prev,
        [module]: allSelected ? [] : [...actions],
      })
    );
  };

  /** Select column */
  const toggleActionColumn = (action: string) => {
    const modulesWithAction = allModules.filter(
      ([_, actions]) => actions.includes(action)
    );

    const allSelected = modulesWithAction.every(([module]) =>
      (permissions[module] || []).includes(action)
    );

    setPermissions((prev) => {
      const updated = { ...prev };

      modulesWithAction.forEach(([module]) => {
        const current = updated[module] || [];

        if (action === "view") {
          updated[module] = allSelected ? [] : ["view"];
        } else {
          if (allSelected) {
            updated[module] = current.filter((a) => a !== action);
          } else {
            if (!current.includes("view")) {
              updated[module] = ["view", action];
            } else {
              updated[module] = Array.from(new Set([...current, action]));
            }
          }
        }
      });

      return enforceDependencies(updated);
    });
  };

  /** Check column selected */
  const isColumnSelected = (action: string) => {
    const modulesWithAction = allModules.filter(
      ([_, actions]) => actions.includes(action)
    );

    return (
      modulesWithAction.length > 0 &&
      modulesWithAction.every(([module]) =>
        (permissions[module] || []).includes(action)
      )
    );
  };

  /** Check ALL selected */
  const isAllSelected = () => {
    return allModules.every(([module, actions]) =>
      actions.every((a) => (permissions[module] || []).includes(a))
    );
  };

  return (
    <div className="w-full">
      <table className="w-full border text-sm">
        <thead className="bg-gray-100">
          <tr>
            <th className="border px-3 py-2 text-left">
              <input
                type="checkbox"
                checked={isAllSelected()}
                onChange={toggleAll}
              />
              <span className="ml-2">Module</span>
            </th>

            {allActions.map((action) => (
              <th
                key={action}
                className="border px-3 py-2 text-center capitalize"
              >
                <input
                  type="checkbox"
                  checked={isColumnSelected(action)}
                  onChange={() => toggleActionColumn(action)}
                />
                <span className="ml-1">{action}</span>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {allModules.map(([module, actions]) => {
            const current = permissions[module] || [];
            const moduleSelected = actions.every((a) =>
              current.includes(a)
            );

            return (
              <tr key={module} className="hover:bg-gray-50">
                <td className="border px-3 py-2 font-medium capitalize">
                  <input
                    type="checkbox"
                    checked={moduleSelected}
                    onChange={() => toggleModule(module)}
                  />
                  <span className="ml-2">{module === "management" ? "Admin" : module}</span>
                </td>

                {allActions.map((action) => {
                  const allowed = actions.includes(action);

                  return (
                    <td key={action} className="border text-center py-2">
                      {allowed ? (
                        <input
                          type="checkbox"
                          checked={current.includes(action)}
                          disabled={
                            (!current.includes("view") &&
                              action !== "view") ||
                            (isDependencyLocked(module) &&
                              action === "view") ||
                            (module === "management" &&
                              action === "view" &&
                              isManagementDisabled(permissions))
                          }
                          title={
                            module === "management" &&
                              action === "view" &&
                              isManagementDisabled(permissions)
                              ? "Enable Product, Category, Customer and User View first"
                              : isDependencyLocked(module) &&
                                action === "view"
                                ? "Required for Order Create"
                                : ""
                          }
                          onChange={() =>
                            togglePermission(module, action)
                          }
                        />
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}